<script setup lang="ts">
/**
 * 消息宿主。挂在 App.vue 顶层，全局只有一个。
 *
 * 用 TransitionGroup 而不是 Transition：多条消息同时进出时，
 * 后面的要平滑上移（move-class），否则会「啪」地跳一格。
 *
 * ## 层级必须高于对话框与抽屉
 *
 * 原来写死 `z-[2000]`，而 Element Plus 的 overlay 走 `nextZIndex()`，
 * **从 2001 起**（`defaultInitialZIndex = 2000`，先自增再返回）—— 于是任何
 * `el-dialog` / `el-drawer` 都压在消息条上面。症状是最坏的一种：弹窗里提交失败，
 * 唯一的失败信号被弹窗自己盖住，操作看起来像成功了。
 * 用 `--mtp-z-toast`（层级令牌表里本来就为消息留的那一档，见 `styles/tokens.scss`）。
 */
import { useToast, type ToastKind } from '@/composables/useToast'

const toast = useToast()

const TONE: Record<ToastKind, string> = {
  success: 'border-status-ok/30 bg-status-ok/10 text-status-ok',
  error: 'border-destructive/30 bg-destructive/10 text-destructive',
  warning: 'border-status-pending/30 bg-status-pending/10 text-status-pending',
  info: 'border-border bg-card text-foreground',
}
</script>

<template>
  <Teleport to="body">
    <TransitionGroup
      tag="div"
      class="pointer-events-none fixed inset-x-0 top-4 flex flex-col items-center gap-2 px-4"
      :style="{ zIndex: 'var(--mtp-z-toast)' }"
      data-test="toast-host"
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2 scale-95"
      leave-active-class="transition duration-150 ease-in absolute"
      leave-to-class="opacity-0 scale-95"
      move-class="transition-transform duration-200"
    >
      <div
        v-for="t in toast.items.value"
        :key="t.id"
        role="status"
        :class="[
          'pointer-events-auto flex max-w-lg items-start gap-2 rounded-xl border px-4 py-2.5 text-sm shadow-md backdrop-blur',
          TONE[t.kind],
        ]"
        @click="toast.dismiss(t.id)"
      >
        <span class="leading-relaxed">{{ t.text }}</span>
      </div>
    </TransitionGroup>
  </Teleport>
</template>
